// src/pages/GoogleAdsProfile.jsx
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getAuthHeaders } from "../services/authHeaders";

// Normaliza sempre para string
const norm = (v) => (v ?? "").toString().trim();

const PROXY_BASE_URL = "https://api.sobevenda.com.br/api-proxy/";

/**
 * Busca os SobeScripts vinculados a um google_id (long) usando o endpoint
 * correto `/script/list-sobescripts`. Tenta GET com querystring, depois
 * POST com body. Se o servidor não filtrar, filtra no cliente comparando
 * `google_ads_account_id` === googleId normalizado.
 */
async function getScriptsByGoogleId(googleId, headers) {
  const gid = norm(googleId);

  // 1) Tenta GET com filtro via querystring
  let resp = await fetch(
    `${PROXY_BASE_URL}script/list-sobescripts?google_ads_account_id=${encodeURIComponent(
      gid
    )}`,
    { headers }
  );
  let data;
  try {
    data = await resp.json();
  } catch {
    data = [];
  }

  let list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  let filtered = list.filter((s) => norm(s.google_ads_account_id) === gid);

  // 2) Se ainda vazio, tenta POST com body
  if (filtered.length === 0) {
    resp = await fetch(`${PROXY_BASE_URL}script/list-sobescripts`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ google_ads_account_id: gid }),
    });
    try {
      data = await resp.json();
    } catch {
      data = [];
    }
    list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    filtered = list.filter((s) => norm(s.google_ads_account_id) === gid);
  }

  return filtered;
}

function GoogleAdsProfile() {
  const { id } = useParams(); // id do registro em google_ads_accounts
  const [account, setAccount] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // 1) Perfil da conta selecionada
        const accRes = await fetch(`${PROXY_BASE_URL}affiliate/google-ads-accounts/${id}`, {
          headers: getAuthHeaders(),
        });
        const accData = await accRes.json();
        setAccount(accData);

        // ⚠️ Use SEMPRE o google_id (ID longo) para bater com a coluna google_ads_account_id em scripts
        const googleId = norm(accData?.google_id);

        // 2) Busca os scripts desta conta
        const filtered = await getScriptsByGoogleId(googleId, getAuthHeaders());
        setScripts(filtered);
      } catch (err) {
        console.error("Erro ao buscar dados do perfil do Google Ads:", err);
        setAccount(null);
        setScripts([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  if (loading) return <div className="p-6">Carregando dados...</div>;
  if (!account) return <div className="p-6 text-red-600">Conta não encontrada.</div>;

  const googleIdShown = norm(account?.google_id);
  const createdAt = account?.created_at
    ? new Date(account.created_at).toLocaleDateString("pt-BR")
    : "-";

  return (
    <div className="p-6">
      {/* Cabeçalho */}
      <div className="flex items-center gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow mb-6">
        <div className="w-16 h-16 bg-gray-200 dark:bg-slate-800 rounded-full flex items-center justify-center font-bold text-xl text-blue-600">
          {account.email?.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold">
            Perfil — <span className="font-mono">{account.email}</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            ID (customer): {account.customer_id}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">google_ads_account_id</span> (filtro):{" "}
            <span className="font-mono">{googleIdShown}</span>
          </p>
        </div>
      </div>

      {/* Métricas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-4 border border-green-100 dark:border-green-900/40 shadow">
          <p className="text-xs text-gray-600 dark:text-gray-300">CRIADO EM</p>
          <h3 className="text-2xl font-bold mt-1">{createdAt}</h3>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800 shadow">
          <p className="text-xs text-gray-600 dark:text-gray-300">E-MAIL</p>
          <h3 className="text-lg font-semibold mt-1">{account.email}</h3>
        </div>

        <div className="bg-blue-950 text-white rounded-2xl p-4 border border-blue-900 shadow">
          <p className="text-xs opacity-80">SobeScripts (desta conta)</p>
          <h3 className="text-2xl font-bold mt-1">{scripts.length}</h3>
        </div>
      </div>

      {/* Título */}
      <h3 className="text-lg font-semibold mb-3">SobeScripts vinculados a esta conta</h3>

      {/* Lista */}
      {scripts.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 text-gray-600 dark:text-gray-300">
          Nenhum SobeScript encontrado para este <span className="font-mono">google_ads_account_id</span>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {scripts.map((s, idx) => (
            <div
              key={`${s.uuid || s.id || idx}`}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 shadow"
            >
              <h4 className="text-lg font-bold mb-1">
                {s.project_name || s.name || "Sem nome"}
              </h4>

              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <p>
                  <span className="font-medium">UUID:</span>{" "}
                  <span className="font-mono break-all">
                    {s.uuid || "-"}
                  </span>
                </p>
                <p>
                  <span className="font-medium">Plataforma:</span> {s.platform || "-"}
                </p>
                <p>
                  <span className="font-medium">google_ads_account_id:</span>{" "}
                  <span className="font-mono">{norm(s.google_ads_account_id)}</span>
                </p>
                {s.selected_pixel_id && (
                  <p>
                    <span className="font-medium">ID da Ação de Conversão:</span>{" "}
                    <span className="font-mono">{s.selected_pixel_id}</span>
                  </p>
                )}
                {s.google_account_name && (
                  <p>
                    <span className="font-medium">Conta Google Ads:</span>{" "}
                    {s.google_account_name}
                  </p>
                )}
                {s.google_account_timezone && (
                  <p>
                    <span className="font-medium">Fuso Horário:</span>{" "}
                    {s.google_account_timezone}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GoogleAdsProfile;
